<template>
  <div class="fields-section">
    <h3>{{ t('customData.fieldsEditor.title') }}</h3>
    <div class="datatable-container">
      <DataTable
        :key="refreshViewComponent"
        :value="fields"
        edit-mode="row"
        data-key="id"
        row-reorder
        :row-class="getRowClass"
        table-class="fields-datatable"
        :pt="{ table: { style: 'min-width: 100%' } }"
        @row-reorder="onFieldOrderChange"
      >
        <template #empty>
          <div class="empty-state">
            <i class="fas fa-layer-group empty-state-icon" />
            <p>{{ t('customData.fieldsEditor.noFieldsDefined') }}</p>
          </div>
        </template>
        <Column
          row-reorder
          header-style="width: 1.5rem; padding: 0;"
          body-style="cursor: grab; user-select: none; text-align: center; padding: 0.5rem 0.1rem;"
          :reorderable-column="false"
        />
        <Column
          field="label"
          header-style="width: 16%"
        >
          <template #header>
            {{ t('customData.fieldsEditor.columnHeaders.label') }}
            <i
              v-tooltip="t('customData.fieldsEditor.tooltips.fieldLabelWithNameHint')"
              class="pi pi-info-circle ml-tooltip"
            />
          </template>
          <template #body="{ data }">
            <div class="form-field-group">
              <div class="label-row">
                <BaseTextInput
                  v-model="data.label"
                  type="text"
                  :disable="!fieldOnEdit(data)"
                  class="w-full"
                  @update:model-value="onLabelInput(data)"
                />
                <i
                  v-if="fieldOnEdit(data)"
                  v-tooltip="t('customData.fieldsEditor.tooltips.addDescription')"
                  class="fas field-desc-toggle"
                  :class="descriptionOpenId === data.id ? 'fa-chevron-up field-desc-toggle--active' : 'fa-chevron-down'"
                  @click="toggleDescription(data.id)"
                />
              </div>
              <textarea
                v-if="fieldOnEdit(data) && descriptionOpenId === data.id"
                v-model="data.description"
                class="field-desc-textarea"
                rows="2"
                :placeholder="t('customData.fieldsEditor.expansion.descriptionPlaceholder')"
              />
              <div
                v-if="data.name"
                v-tooltip="t('customData.fieldsEditor.tooltips.fieldNameHint')"
                class="field-name-hint"
              >
                <i class="fas fa-code" />
                {{ data.name }}
              </div>
            </div>
          </template>
        </Column>
        <Column
          header-style="width: 3rem; padding: 0;"
          body-style="padding: 0.5rem 0.15rem;"
        >
          <template #body="{ data }">
            <div class="icons-cell">
              <i
                v-if="!fieldOnEdit(data) && data.description"
                v-tooltip="'Description: ' + data.description"
                class="fas fa-comment-alt field-desc-icon--has-content"
              />
              <i
                v-if="data.unique_key"
                v-tooltip="t('customData.fieldsEditor.tooltips.uniqueKey')"
                class="fas fa-key primary-key-icon"
              />
              <i
                v-if="hasRelation(data)"
                v-tooltip="t('customData.fieldsEditor.tooltips.fieldRelation')"
                class="fas fa-link relation-icon"
              />
              <i
                v-else-if="isFieldEligibleForRelation(data)"
                v-tooltip="t('customData.fieldsEditor.tooltips.fieldCanBeUsedInRelation')"
                class="fas fa-link relation-icon-potential"
              />
            </div>
          </template>
        </Column>
        <Column
          field="type"
          header-style="width: 28%"
        >
          <template #header>
            {{ t('customData.fieldsEditor.columnHeaders.type') }}
            <i
              v-tooltip="t('customData.fieldsEditor.tooltips.fieldType')"
              class="pi pi-info-circle ml-tooltip"
            />
          </template>
          <template #body="{ data }">
            <div
              class="type-cell-group"
              :class="{ 'type-cell-group--with-options': data.type === AvailableTypeEnum.LIST && fieldOnEdit(data) }"
            >
              <div class="type-cell-selector">
                <BaseDropdown
                  v-if="fieldOnEdit(data)"
                  v-model="data.type"
                  :options="typeAvailable"
                  @update:model-value="handleFieldTypeChange(data)"
                />
                <span
                  v-else
                  class="type-badge"
                  :class="`type-badge--${data.type}`"
                >
                  {{ t(`customData.types.${data.type}`) }}
                </span>
                <!-- Mode lecture LIST : icone info avec tooltip des options -->
                <i
                  v-if="data.type === AvailableTypeEnum.LIST && !fieldOnEdit(data) && data.options && data.options.length > 0"
                  v-tooltip="formatListOptionsTooltip(data.options)"
                  class="fas fa-info-circle list-options-info-icon"
                />
              </div>
              <!-- Mode edition LIST : autocomplete inline -->
              <div
                v-if="data.type === AvailableTypeEnum.LIST && fieldOnEdit(data)"
                class="list-options-inline"
              >
                <AutoComplete
                  v-model="data.options"
                  multiple
                  :delay="500"
                  :allow-duplicate="false"
                  :force-selection="false"
                  :suggestions="optionSuggestions"
                  :placeholder="t('customData.fieldsEditor.expansion.addOptionsPlaceholder')"
                  class="w-full spm-auto-complete"
                  @complete="optionSuggestions = $event.query.trim() ? [$event.query.trim()] : []"
                  @keydown.enter="addNewOptionToField($event, data)"
                  @item-select="removeDuplicateOption($event, data)"
                />
              </div>
            </div>
          </template>
        </Column>
        <Column
          field="required"
          header-style="width: 5%"
          body-style="text-align: center;"
        >
          <template #header>
            {{ t('customData.fieldsEditor.columnHeaders.required') }}
            <i
              v-tooltip="t('customData.fieldsEditor.tooltips.fieldRequired')"
              class="pi pi-info-circle ml-tooltip"
            />
          </template>
          <template #body="{ data }">
            <Checkbox
              v-model="data.required"
              :binary="true"
              :disabled="!fieldOnEdit(data)"
              @change="onRequiredChange(data)"
            />
          </template>
        </Column>
        <Column
          field="default"
          header-style="width: 13%"
        >
          <template #header>
            {{ t('customData.fieldsEditor.columnHeaders.default') }}
            <i
              v-tooltip="t('customData.fieldsEditor.tooltips.fieldDefault')"
              class="pi pi-info-circle ml-tooltip"
            />
          </template>
          <template #body="{ data }">
            <BaseDropdown
              v-if="data.type === AvailableTypeEnum.LIST"
              v-model="data.default"
              :options="data.options"
              :disabled="!fieldOnEdit(data) || !data.options || data.options.length === 0"
              :placeholder="t('customData.fieldsEditor.placeholders.chooseDefault')"
              class="w-full"
            />
            <BaseTextInput
              v-else-if="[AvailableTypeEnum.TEXT, AvailableTypeEnum.LONGTEXT, AvailableTypeEnum.JSON].includes(data.type)"
              v-model="data.default"
              type="text"
              class="w-full"
              :placeholder="t('customData.fieldsEditor.placeholders.null')"
              :disable="!fieldOnEdit(data)"
            />
            <BaseInputNumber
              v-else-if="[AvailableTypeEnum.NUMBER, AvailableTypeEnum.DECIMAL].includes(data.type)"
              v-model="data.default"
              class="w-full"
              :disabled="!fieldOnEdit(data)"
              input-width="2rem"
              :placeholder="t('customData.fieldsEditor.placeholders.null')"
            />
            <BaseCalendar
              v-else-if="[AvailableTypeEnum.DATE].includes(data.type)"
              v-model="data.default"
              class="w-full"
              :disabled="!fieldOnEdit(data)"
              :hide-on-select="false"
              type="date"
            />
            <BaseCalendar
              v-else-if="[AvailableTypeEnum.DATETIME].includes(data.type)"
              v-model="data.default"
              class="w-full"
              :disabled="!fieldOnEdit(data)"
              :hide-on-select="false"
              type="datetime"
            />
            <Checkbox
              v-else-if="[AvailableTypeEnum.BOOL].includes(data.type)"
              v-model="data.default"
              :binary="true"
              :disabled="!fieldOnEdit(data)"
            />
            <span
              v-else-if="data.type === AvailableTypeEnum.GEOLOCATION"
              class="text-color-secondary italic"
            >
              N/A
            </span>
          </template>
        </Column>
        <Column
          field="unique_key"
          header-style="width: 5%"
          body-style="text-align: center;"
        >
          <template #header>
            {{ t('customData.fieldsEditor.columnHeaders.unique') }}
            <i
              v-tooltip="t('customData.fieldsEditor.tooltips.unique')"
              class="pi pi-info-circle ml-tooltip"
            />
          </template>
          <template #body="{ data }">
            <Checkbox
              v-model="data.unique_key"
              :binary="true"
              :disabled="!fieldOnEdit(data) || !isUniqueKeyEligible(data)"
              @change="onUniqueKeyChange(data)"
            />
          </template>
        </Column>
        <Column
          header-style="width: 3.5rem"
          body-style="text-align: center"
        >
          <template #body="{ data }">
            <SpmOverlayPanel
              v-if="getFieldActions(data).length > 0"
              class-trigger="flex align-items-center justify-content-center action-renderer-trigger"
              class-content="flex align-items-center justify-content-center spm-overlay-panel-content"
              :close-panel="closeActionsPanel"
              @onclose-panel="closeActionsPanel = false"
            >
              <template #trigger>
                <i class="far fa-ellipsis-v fa-2x" />
              </template>
              <SpmPanelMenu
                :items="getFieldActions(data)"
              />
            </SpmOverlayPanel>
          </template>
        </Column>

      </DataTable>
    </div>

    <div class="add-field-container">
      <div
        class="add-field-item"
        @click="addNewEmptyField"
      >
        <span class="add-field-icon-circle">
          <i class="fas fa-plus" />
        </span>
        <span>{{ t('customData.fieldsEditor.addFieldButton') }}</span>
      </div>
    </div>
  </div>
  <ConfirmDialog />
  <ConfirmPopup group="basicGroup" />
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  nextTick,
} from 'vue';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import BaseCalendar from '@/components/fields/BaseCalendar.vue';
import { useI18n } from 'vue-i18n';
import DataTable, { DataTableRowReorderEvent } from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Message from 'primevue/message';
import AutoComplete from 'primevue/autocomplete';
import Chip from 'primevue/chip';
import ConfirmDialog from 'primevue/confirmdialog';
import Checkbox from 'primevue/checkbox';
import Tooltip from 'primevue/tooltip';
import { showToastError } from '@/helpers';
import { validateFieldWithVuelidate } from '@/composables/custom-data/customData.validations';
import {
  Field, AvailableTypeEnum, UNIQUE_KEY_ELIGIBLE_TYPES,
  ForeignKeyRow,
} from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmPopup from 'primevue/confirmpopup';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmPanelMenu from '@/components/spm-primevue/SpmPanelMenu.vue';

export default defineComponent({
  name: 'SchemaFieldsEditor',

  components: {
    DataTable,
    Column,
    Button,
    Message,
    AutoComplete,
    ConfirmDialog,
    Chip,
    BaseDropdown,
    BaseTextInput,
    BaseInputNumber,
    BaseCalendar,
    Checkbox,
    ConfirmPopup,
    SpmOverlayPanel,
    SpmPanelMenu,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const confirm = useConfirm();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const isEditMode = computed(() => store.getters['customObject/isEditMode']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const refreshViewComponent = computed(() => store.getters['customObject/getRefreshViewComponent']);
    const overrides = computed(() => store.getters['customObject/getOverrides']);
    const existingFields = computed(() => store.getters['customObject/getNeededDataExistingFields'] || []);
    const descriptionOpenId = ref<number | null>(null);
    const closeActionsPanel = ref(false);

    // check if the field is editable
    const isFieldEditable = (field: Field): boolean => !existingFields.value.some((f: any) => f.id === field.id);
    // check if the field is on edit
    const fieldOnEdit = (field: Field): boolean => currentEditingElement.value
     && currentEditingElement.value.id === field.id
      && currentEditingElement.value.elementType === 'field';
    // check if the field is a new add field
    const isNewAddField = (field: Field): boolean => currentEditingElement.value
     && currentEditingElement.value.id === field.id
      && currentEditingElement.value.elementType === 'field'
       && currentEditingElement.value.isNew;

    const typeAvailable = ref(
      Object.values(AvailableTypeEnum).map((type) => ({
        label: t(`customData.types.${type}`),
        value: type,
      })),
    );

    // helper functions
    const cloneField = (field: Field): Field => JSON.parse(JSON.stringify(field));
    const stripDiacritics = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const labelToName = (label: string) => stripDiacritics(label)
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/^_+|_+$/g, '')
      .substring(0, 50);

    // Auto-generate name from label (only for new fields)
    const onLabelInput = (field: Field) => {
      if (isNewAddField(field)) {
        // eslint-disable-next-line no-param-reassign
        field.name = labelToName(field.label);
      }
    };

    // Toggle description textarea visibility
    const toggleDescription = (id: number) => {
      descriptionOpenId.value = descriptionOpenId.value === id ? null : id;
    };

    /**
     * Basic field functions
     */
    // check if the field is eligible for unique key
    const isUniqueKeyEligible = (fieldRowData: Field): boolean => UNIQUE_KEY_ELIGIBLE_TYPES.includes(fieldRowData.type);

    // check if the field is eligible for relation (must be text or number)
    const isFieldEligibleForRelation = (fieldRowData: Field): boolean => ['text', 'number'].includes(fieldRowData.type);

    // check if the field has a relation
    const hasRelation = (fieldRowData: Field): boolean => relationships.value.some(
      (rel: { localField: string; isEditing: any }) => rel.localField === fieldRowData.name && !rel.isEditing,
    );

    // check if the field is used in an override
    const isFieldUsedInOverride = (fieldRowData: Field): boolean => overrides.value.some((o: any) => o.custom_field_name === fieldRowData.name
     && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));

    /**
     * Basic DataTables functions
     */
    // get the class of the row depending on the state of the field (isEditing)
    const getRowClass = (fieldRowData: Field): string | undefined => (fieldOnEdit(fieldRowData) ? 'row-is-editing' : undefined);

    /**
     * Actions on fields functions (add, remove, edit, cancel edit, save, update) on the store
     */

    // add a new empty field
    const addNewEmptyField = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.fieldsEditor.actions.addNewEmpty'), 4000);
        return;
      }
      store.dispatch('customObject/handleFieldAction', { action: 'add-new-empty' });
    };

    // remove a field
    const removeField = (event: any, field: Field) => {
      confirm.require({
        group: 'basicGroup',
        target: event.currentTarget,
        header: t('confirmation'),
        message: t('customData.fieldsEditor.actions.deleteField.confirmMessage'),
        icon: 'fas fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        acceptClass: 'p-button-success',
        rejectClass: 'p-button-secondary',
        accept: () => {
          // verify if the field is used in a relation
          const usedInRelation = relationships.value.find((rel: ForeignKeyRow) => rel.localField === field.name);
          // verify if the field is used in an override
          const usedInOverride = overrides.value.find((o: any) => o.custom_field_name === field.name && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));

          if (usedInRelation || usedInOverride) {
            confirm.require({
              header: t('confirmation'),
              message: t('customData.fieldsEditor.actions.deleteField.confirmMessage2'),
              icon: 'fas fa-exclamation-triangle',
              acceptLabel: t('yes'),
              rejectLabel: t('no'),
              acceptClass: 'p-button-success',
              rejectClass: 'p-button-secondary',
              accept: () => {
                store.dispatch('customObject/handleFieldAction', { action: 'remove', id: field.id });
                nextTick(() => { closeActionsPanel.value = true; });
              },
              reject: () => { nextTick(() => { closeActionsPanel.value = true; }); },
            });
          } else {
            store.dispatch('customObject/handleFieldAction', { action: 'remove', id: field.id });
            nextTick(() => { closeActionsPanel.value = true; });
          }
        },
        reject: () => { nextTick(() => { closeActionsPanel.value = true; }); },
      });
    };

    // start editing a field
    const editField = (id: number) => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.fieldsEditor.actions.editField'), 4000);
        return;
      }
      store.dispatch('customObject/handleFieldAction', { action: 'edit', id });
    };

    // cancel editing a field
    const cancelEditField = (id: number) => store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });

    // update a field when dynamicly changing the field data
    const updateField = (id: number, fieldCloned: Field) => {
      const fieldIndex = fields.value.findIndex((field: Field) => field.id === id);
      if (fieldIndex !== -1) {
        store.dispatch('customObject/handleFieldAction', { action: 'instant-update-data', id, fieldData: { ...fieldCloned } });
      }
    };

    const isFieldCompatibleWithOverride = (field: Field, override: any): boolean => {
      // Check if the field is not a unique key
      if (field.unique_key) return false;
      // Check if the field is not used in a relation
      const isUsedInRelation = relationships.value.some((rel: ForeignKeyRow) => rel.localField === field.name);
      if (isUsedInRelation) return false;
      // Check if the type of the field is compatible with the system field
      const targetSchema = override.target_system_schema;
      const overridableFields = OVERRIDABLE_FIELDS[targetSchema as keyof typeof OVERRIDABLE_FIELDS] || [];
      const systemField = overridableFields.find((sf: any) => sf.field === override.system_field_name);

      if (!systemField) return false;
      // The type of the custom field must correspond to the type of the system field (if not, the override will be deleted)
      const systemFieldTypes = Array.isArray(systemField.type) ? systemField.type : [systemField.type];
      return systemFieldTypes.includes(field.type as string);
    };

    // save a field
    const saveField = (id: number) => {
      const fieldIndex = fields.value.findIndex((field: Field) => field.id === id);
      if (fieldIndex === -1) {
        return;
      }

      const errorsFields = [];
      const currentField = fields.value[fieldIndex];
      // Gather names of other fields to check for uniqueness
      const otherNames = fields.value.filter((f: Field, idx: number) => idx !== fieldIndex).map((f: Field) => f.name);
      // Validate the current field with Vuelidate
      const validationResult = validateFieldWithVuelidate(t, currentField, otherNames);
      if (!validationResult.valid) {
        // Store errors and display them as toast notifications
        Object.entries(validationResult.errors).forEach(([fieldKey, messages]) => {
          errorsFields.push(fieldKey);
          messages.forEach((msg: string) => {
            showToastError(msg, 6000);
          });
        });
        return;
      }

      // Check for geolocation name collisions
      const otherFields = fields.value.filter((f: Field, idx: number) => idx !== fieldIndex);
      if (currentField.type === AvailableTypeEnum.GEOLOCATION) {
        const conflicting = otherFields.find((f: Field) => f.name === `${currentField.name}_lat` || f.name === `${currentField.name}_lng`);
        if (conflicting) {
          showToastError(t('customData.validation.geolocation_name_collision', [currentField.name, conflicting.name]), 6000);
          return;
        }
      } else if (currentField.name.endsWith('_lat') || currentField.name.endsWith('_lng')) {
        const baseName = currentField.name.replace(/_(lat|lng)$/, '');
        const geoField = otherFields.find((f: Field) => f.name === baseName && f.type === AvailableTypeEnum.GEOLOCATION);
        if (geoField) {
          showToastError(t('customData.validation.geolocation_name_collision_reverse', [currentField.name, baseName]), 6000);
          return;
        }
      }

      // check if the field is in relation and if the new properties are compatible
      if (hasRelation(currentEditingElement.value)) {
        const isTypeCompatible = isFieldEligibleForRelation(currentField);

        if (!isTypeCompatible) {
          const issuesText = t('customData.fieldsEditor.actions.saveField.compatibility.type', [currentField.type]);
          confirm.require({
            header: t('customData.fieldsEditor.confirm.relationConflictHeader'),
            message: t('customData.fieldsEditor.confirm.relationConflictMessage', [currentField.name, issuesText]),
            icon: 'fas fa-exclamation-triangle',
            acceptLabel: t('customData.fieldsEditor.confirm.deleteRelationAndSave'),
            rejectLabel: t('customData.fieldsEditor.confirm.cancelModifications'),
            acceptClass: 'p-button-danger',
            // Si l'utilisateur accepte, supprimer la relation et sauvegarder
            accept: () => {
              // get the relation id
              const relationIdToDelete = relationships.value.find((rel: ForeignKeyRow) => rel.localField === currentEditingElement.value.name)?.id;
              if (relationIdToDelete) {
                store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
                store.dispatch('customObject/handleRelationAction', { action: 'remove', id: relationIdToDelete });
              }
            },
            reject: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });
            },
          });
        } else {
          store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
        }
      } else if (isFieldUsedInOverride(currentEditingElement.value)) {
        const usedOverride = overrides.value.find((o: any) => o.custom_field_name === currentEditingElement.value.name
         && parseInt(o.id_custom_data_definition, 10) === parseInt(idCustomDataDefinition.value, 10));
        if (usedOverride && !isFieldCompatibleWithOverride(currentField, usedOverride)) {
          confirm.require({
            header: t('customData.fieldsEditor.confirm.overrideConflictHeader'),
            message: t('customData.fieldsEditor.confirm.overrideConflictMessage', [currentEditingElement.value.name]),
            icon: 'fas fa-exclamation-triangle',
            acceptLabel: t('customData.fieldsEditor.confirm.deleteOverrideAndSave'),
            rejectLabel: t('customData.fieldsEditor.confirm.cancelModifications'),
            acceptClass: 'p-button-success',
            accept: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
              store.dispatch('customObject/handleOverrideAction', { action: 'remove', id: usedOverride.id });
            },
            reject: () => {
              store.dispatch('customObject/handleFieldAction', { action: 'cancel-edit', id });
            },
          });
        } else {
          // if new field name is different from the old one, update the override
          if (currentField.name !== currentEditingElement.value.name) {
            store.dispatch('customObject/handleOverrideAction', { action: 'update', id: usedOverride.id, overrideItem: { ...usedOverride, custom_field_name: currentField.name } });
          }
          store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
        }
      } else {
        // If validation passes and no relation conflicts, emit the save action
        store.dispatch('customObject/handleFieldAction', { action: 'save', id, fieldData: { ...currentField } });
      }
    };

    /**
     * Advanced field functions
     */
    const optionSuggestions = ref<string[]>([]);

    // function to handle when the type of a field is changed
    const handleFieldTypeChange = (field: Field) => {
      const fieldCloned = cloneField(field);
      if (!fieldCloned) return;

      // remove unique_key if the new type is not eligible
      if (!isUniqueKeyEligible(fieldCloned) && fieldCloned.unique_key) {
        fieldCloned.unique_key = false;
      }

      // if the new type is 'list', ensure options is an array
      if (fieldCloned.type === AvailableTypeEnum.LIST) {
        if (!Array.isArray(field.options)) {
          fieldCloned.options = [];
        }
      } else {
        if (fieldCloned.options) {
          delete fieldCloned.options;
        }
      }

      // always reset the default value when type changes
      fieldCloned.default = null;

      // Geolocation fields: force unique_key=false (not indexable as unique)
      if (fieldCloned.type === AvailableTypeEnum.GEOLOCATION) {
        fieldCloned.unique_key = false;
      }

      // trigger the update for this field on the store with the new field data
      updateField(field.id, fieldCloned);
    };

    // add a new option to the field when pressing Enter (only for list type fields / auto complete)
    const addNewOptionToField = (event: KeyboardEvent, field: Field) => {
      const inputElement = event.target as HTMLInputElement;
      const value = inputElement.value.trim();
      const fieldCloned = cloneField(field);

      if (value && fieldCloned) {
        // if the field has no options, initialize an empty array
        if (!Array.isArray(fieldCloned.options)) {
          fieldCloned.options = [];
        }
        // if the option is not already in the field, add it
        if (!fieldCloned.options.some((opt: string) => opt.toLowerCase() === value.toLowerCase())) {
          fieldCloned.options = [...fieldCloned.options, value];
          updateField(field.id, fieldCloned);
          optionSuggestions.value = [];
          event.preventDefault();
          window.requestAnimationFrame(() => {
            if (inputElement) inputElement.value = '';
          });
        } else {
          // if the option is already in the field, prevent adding it and clear the input
          event.preventDefault();
          window.requestAnimationFrame(() => {
            if (inputElement) inputElement.value = '';
          });
        }
      }
    };

    // remove duplicate options when an option is selected (only for list type fields / auto complete)
    const removeDuplicateOption = (event: { value: string }, field: Field) => {
      const fieldCloned = cloneField(field);
      if (!fieldCloned || !Array.isArray(fieldCloned.options)) return;

      const { value: selectedValue } = event;
      const { options } = fieldCloned;

      // Find duplicate (ignoring the last added item)
      const duplicateIndex = options.findIndex(
        (opt: string, index: number) => index !== options.length - 1
          && opt.toLowerCase() === selectedValue.toLowerCase(),
      );

      // Remove the last entry if duplicate found
      if (duplicateIndex !== -1) {
        fieldCloned.options = options.slice(0, -1);
        updateField(field.id, fieldCloned);
      }
    };

    const onFieldOrderChange = (event: DataTableRowReorderEvent) => {
      // if not in edit mode, update the order in the store without checking for existing fields
      if (!isEditMode.value) {
        store.commit('customObject/setFields', event.value);
        return;
      }

      // if in edit mode, don't allow to move fields that already exist in the custom data definition
      const oldOrder = fields.value.map((f: Field) => f.id);
      const newOrder = event.value.map((f: Field) => f.id);

      // Get existing fields from store and filter current fields accordingly
      const existingIds = existingFields.value.map((f: any) => f.id);
      let existingMoved = false;
      for (let i = 0; i < existingIds.length; i++) {
        if (oldOrder.indexOf(existingIds[i]) !== newOrder.indexOf(existingIds[i])) {
          existingMoved = true;
          break;
        }
      }
      if (!existingMoved) {
        // update the order in the store
        store.commit('customObject/setFields', event.value);
      }
    };

    // handle the change of the unique key of a field
    const onUniqueKeyChange = (field: Field) => {
      const fieldCloned = cloneField(field);

      // if the field is a unique key, set the required field to true (unique key fields are always required)
      if (fieldCloned.unique_key) {
        fieldCloned.required = true;
      }
      updateField(field.id, fieldCloned);
    };

    // handle the change of the required field of a field
    const onRequiredChange = (field: Field) => {
      const fieldCloned = cloneField(field);
      // if new required value is false, set the unique key field to false
      if (!fieldCloned.required) {
        fieldCloned.unique_key = false;
      }
      updateField(field.id, fieldCloned);
    };

    const formatListOptionsTooltip = (options: string[]): string => options.join(', ');

    // Close the actions panel after executing an action
    const closeAndRun = (fn: (...args: any[]) => void) => (...args: any[]) => {
      fn(...args);
      nextTick(() => { closeActionsPanel.value = true; });
    };

    // Build menu items for field actions
    const getFieldActions = (field: Field) => {
      if (fieldOnEdit(field)) {
        const items: any[] = [
          {
            label: t('customData.fieldsEditor.tooltips.save'),
            icon: 'fa-solid fa-save',
            callback: closeAndRun(() => saveField(field.id)),
          },
        ];
        if (!isNewAddField(field)) {
          items.push({
            label: t('customData.fieldsEditor.tooltips.cancel'),
            icon: 'fa-solid fa-times',
            callback: closeAndRun(() => cancelEditField(field.id)),
          });
        }
        items.push({ separator: true });
        items.push({
          label: t('customData.fieldsEditor.tooltips.delete'),
          icon: 'fa-regular fa-trash-alt',
          class: 'danger',
          callback: (_data: any, event: any) => removeField(event, field),
        });
        return items;
      }

      if (isFieldEditable(field)) {
        return [
          {
            label: t('customData.fieldsEditor.tooltips.edit'),
            icon: 'fa-solid fa-pencil-alt',
            callback: closeAndRun(() => editField(field.id)),
          },
          { separator: true },
          {
            label: t('customData.fieldsEditor.tooltips.delete'),
            icon: 'fa-regular fa-trash-alt',
            class: 'danger',
            callback: (_data: any, event: any) => removeField(event, field),
          },
        ];
      }

      return [];
    };

    return {
      t,
      refreshViewComponent,
      fields,
      isFieldEditable,
      fieldOnEdit,
      isNewAddField,
      getRowClass,
      isFieldEligibleForRelation,
      isUniqueKeyEligible,
      hasRelation,
      onLabelInput,
      descriptionOpenId,
      toggleDescription,
      // store actions on fields and field functions
      addNewEmptyField,
      removeField,
      editField,
      cancelEditField,
      saveField,
      handleFieldTypeChange,
      optionSuggestions,
      addNewOptionToField,
      removeDuplicateOption,
      onFieldOrderChange,
      onUniqueKeyChange,
      onRequiredChange,
      formatListOptionsTooltip,
      getFieldActions,
      closeActionsPanel,
      // other
      AvailableTypeEnum,
      typeAvailable,
    };
  },
});
</script>

<style lang="scss">
.spm-auto-complete {
  .p-inputtext {
    box-shadow: none !important;
    border-color: $heather !important;
    height: unset !important;
    min-height: $field-default-height !important;
  }

  .p-autocomplete-token {

  }
}
</style>

<style lang="scss" scoped>
.fields-section {
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.2em;
    color: #333;
  }
}

/* Conteneur avec bordures arrondies */
.datatable-container {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

/* Styles pour DataTable */
:deep(.fields-datatable) {
  border: none !important;
  border-radius: 0 !important;

  .p-datatable-wrapper {
    border-radius: 0 !important;
    border: none !important;
  }

  .p-datatable-table {
    border-radius: 0 !important;
    border: none !important;
  }

  .p-datatable-thead {
    border-radius: 0 !important;
  }

  /* Header ameliore — fond blanc, uppercase, compact */
  .p-datatable-thead > tr > th {
    background-color: #ffffff;
    color: #888;
    font-weight: 600;
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.6rem 0.35rem;
    border-bottom: 1px solid #f0f0f0;
    border-top: none;
    border-left: none;
    border-right: none;
  }

  .p-datatable-tbody > tr {
    background-color: #ffffff;
    border: none;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
    }

    /* Ligne en edition — accent vert a gauche */
    &.row-is-editing {
      background-color: #f0f8e8 !important;
      border-left: 3px solid #94c840;

      td {
        border-bottom-color: #7ca338;
      }

      .editor-input, .editor-select {
        background-color: #fff !important;
      }
      .editor-checkbox:disabled {
        opacity: 0.7;
      }
    }

    &.p-datatable-row-expansion > td {
      border-bottom: none !important;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .p-datatable-tbody > tr > td {
    padding: 0.5rem 0.35rem;
    vertical-align: middle;
    border: none;
    border-left: none;
    border-right: none;
  }

  /* Désactive l'icône drag pour les champs non déplaçables */
  .p-datatable-tbody > tr:not(.row-is-editing) > td:first-child {
    &[style*='not-allowed'] {
      pointer-events: none;
      opacity: 0.5;
    }
  }
}

/* Label row — input + chevron description */
.label-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Colonne icones — flex horizontal compact */
.icons-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 0.85em;
}

/* Chevron toggle description (mode write) */
.field-desc-toggle {
  color: #bbb;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #666;
  }

  &--active {
    color: #94c840;
  }
}

/* Icone description existante (mode read) */
.field-desc-icon--has-content {
  color: #adb5bd;
  cursor: help;
  font-size: 0.8em;

  &:hover {
    color: #666;
  }
}

/* Textarea description inline */
.field-desc-textarea {
  width: 100%;
  margin-top: 4px;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.85em;
  font-family: inherit;
  resize: vertical;
  min-height: 40px;
  color: #333;
  background-color: #fff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #94c840;
  }
}

.no-options {
  color: #999;
  font-style: italic;
  font-size: 0.9em;
}

/* Styles pour les inputs/selects */
.editor-input, .editor-select {
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  font-size: 0.95em;
  box-sizing: border-box;
  &:disabled {
    background-color: #f9f9f9;
    cursor: default;
    opacity: 0.8;
  }
}

.editor-checkbox {
  height: 16px;
  width: 16px;
  cursor: pointer;
}

.default-bool-checkbox {
  margin: 0 auto;
  display: block;
}

/* Styles pour les icones */
.grip-icon {
  color: #adb5bd;
  font-size: 1.1em;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  cursor: grab;
  user-select: none;
}

.relation-icon {
  color: #007bff;
  cursor: help;
}

.relation-icon-potential {
  color: #adb5bd;
  cursor: help;
}

.primary-key-icon {
  color: #ffc107;
  cursor: help;
}

/* Styles pour AutoComplete et Dropdown */
:deep(.editor-autocomplete) {
  .p-autocomplete-input {
    padding: 6px 8px;
    font-size: 0.95em;
    width: 100%;
  }
  .p-autocomplete-multiple-container {
    padding: 3px;
    width: 100%;
  }
}

:deep(.editor-dropdown) {
  .p-dropdown-label {
    padding: 6px 8px;
    font-size: 0.95em;
  }
}

/* Style pour le trigger du menu actions */
:deep(.action-renderer-trigger) {
  cursor: pointer;
  color: #888;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: #333;
  }
}

/* Type cell — flex row quand LIST pour aligner dropdown + options */
.type-cell-group {
  display: flex;
  flex-direction: column;

  &--with-options {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 8px;
  }
}

.type-cell-selector {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.list-options-info-icon {
  color: #e65100;
  font-size: 0.85em;
  cursor: help;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

/* Options LIST inline en mode edition */
.list-options-inline {
  flex: 1;
  min-width: 0;
}

/* Badges de type colores */
.type-badge {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.02em;

  /* text / longtext — bleu doux */
  &--text, &--longtext {
    background-color: #e8f0fe;
    color: #1a73e8;
  }
  /* number / decimal — violet doux */
  &--number, &--decimal {
    background-color: #f3e8ff;
    color: #7c3aed;
  }
  /* bool — vert doux */
  &--bool {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  /* list — orange doux */
  &--list {
    background-color: #fff3e0;
    color: #e65100;
  }
  /* date / datetime — teal doux */
  &--date, &--datetime {
    background-color: #e0f2f1;
    color: #00695c;
  }
  /* json — gris fonce */
  &--json {
    background-color: #f5f5f5;
    color: #616161;
  }
  /* geolocation — rose doux */
  &--geolocation {
    background-color: #fce4ec;
    color: #c62828;
  }
}

/* Zone d'ajout — pleine largeur, plus visible */
.add-field-container {
  margin-top: 1rem;
  display: flex;
}

.add-field-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.85rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-weight: 500;
  border: 1px dashed #ccc;
  color: #666;
  background-color: transparent;

  &:hover {
    border-color: #94c840;
    color: #94c840;
    background-color: #f0f8e8;

    .add-field-icon-circle {
      background-color: #94c840;
      color: #fff;
    }
  }
}

.add-field-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #666;
  font-size: 0.75rem;
  transition: all 0.25s ease;
}

/* field-name-hint ameliore */
.field-name-hint {
  font-size: 0.7em;
  color: #aaa;
  margin-top: 3px;
  font-family: 'SFMono-Regular', Consolas, monospace;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  animation: fadeInHint 0.3s ease forwards;

  i {
    font-size: 0.8em;
  }
}

@keyframes fadeInHint {
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Etat vide ameliore */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: #999;

  p {
    margin: 0;
    font-size: 0.95em;
    font-style: italic;
  }
}

.empty-state-icon {
  font-size: 2.5rem;
  color: #d0d0d0;
  margin-bottom: 0.75rem;
}

/* Info-circle tooltip plus discret */
.ml-tooltip {
  margin-left: 6px;
  opacity: 0.5;
  font-size: 0.85em;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}
</style>
